import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

const PROMO_TYPES = [
  { value: "descuento", label: "Descuento (%)" },
  { value: "precio_fijo", label: "Precio oferta (con IVA)" },
  { value: "mas_vendido", label: "Más Vendido" },
  { value: "destacado", label: "Producto Destacado" },
];

const money = (n) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

export default function Promotion() {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [openAuto, setOpenAuto] = useState(false);
  const [producto, setProducto] = useState(null);
  const [tipoPromo, setTipoPromo] = useState("");
  const [valorPromo, setValorPromo] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [sinFechaFin, setSinFechaFin] = useState(false);
  const [alerta, setAlerta] = useState({ open: false, message: "", type: "success" });
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (busqueda.length < 2 || producto) return;
    (async () => {
      try {
        const resp = await fetch(`/productos/ver-productos/?query=${encodeURIComponent(busqueda)}`, {
          credentials: "include",
        });
        const data = await resp.json();
        const list = Array.isArray(data?.products) ? data.products : [];
        setProductos(list);
      } catch {}
    })();
  }, [busqueda, producto]);

  const seleccionarProducto = (p) => {
    if (!p || typeof p !== "object") return;
    setProducto(p);
    setBusqueda(`${p.name} (${p.code})`);
    setOpenAuto(false);
  };

  const limpiarProducto = () => {
    setProducto(null);
    setBusqueda("");
    setProductos([]);
    setOpenAuto(false);
    setTipoPromo("");
    setValorPromo("");
    setFechaInicio("");
    setFechaFin("");
    setSinFechaFin(false);
  };

  const requiereValor = tipoPromo === "descuento" || tipoPromo === "precio_fijo";

  const precioBase = useMemo(() => (producto ? Number(producto.pvp1 || 0) : 0), [producto]);
  const ivaPctActual = useMemo(() => Number(producto?.iva_porcentaje ?? 0), [producto]);
  const ivaRate = useMemo(() => (Number.isFinite(ivaPctActual) ? ivaPctActual / 100 : 0), [ivaPctActual]);

  const ivaFijoProducto = useMemo(() => {
    const v = producto?.iva_valor ?? producto?.iva_aplicado_valor ?? 0;
    return Number(v || 0);
  }, [producto]);

  const totalProducto = useMemo(() => +(precioBase + ivaFijoProducto).toFixed(2), [precioBase, ivaFijoProducto]);

  const descuentoValor = useMemo(() => {
    const base = precioBase || 0;
    if (!tipoPromo) return 0;

    if (tipoPromo === "descuento") {
      if (valorPromo === "") return 0;
      const pct = Math.max(0, Math.min(100, Number(valorPromo)));
      if (!Number.isFinite(pct)) return 0;
      return +(base * (pct / 100)).toFixed(2);
    }

    if (tipoPromo === "precio_fijo") {
      if (valorPromo === "") return 0;
      const targetTotal = Math.max(0, Number(valorPromo));
      if (!Number.isFinite(targetTotal)) return 0;
      const targetSubtotal = ivaRate > 0 ? targetTotal / (1 + ivaRate) : targetTotal;
      const desc = base - targetSubtotal;
      return +Math.max(0, Math.min(base, desc)).toFixed(2);
    }

    return 0;
  }, [tipoPromo, valorPromo, precioBase, ivaRate]);

  const subtotalPromo = useMemo(() => +Math.max(0, (precioBase || 0) - (descuentoValor || 0)).toFixed(2), [precioBase, descuentoValor]);
  const ivaPromo = useMemo(() => +(subtotalPromo * ivaRate).toFixed(2), [subtotalPromo, ivaRate]);
  const totalPromo = useMemo(() => +(subtotalPromo + ivaPromo).toFixed(2), [subtotalPromo, ivaPromo]);

  const validar = () => {
    if (!producto) return "Selecciona un producto.";
    if (!tipoPromo) return "Selecciona el tipo de promoción.";
    if (requiereValor && (valorPromo === "" || Number(valorPromo) < 0)) return "Ingresa un valor válido.";
    if (!sinFechaFin && fechaFin && fechaInicio && fechaFin < fechaInicio) return "La fecha de fin no puede ser anterior al inicio.";
    return null;
  };

  const handleGuardar = async () => {
    const error = validar();
    if (error) {
      setAlerta({ open: true, message: error, type: "error" });
      return;
    }

    const valueToSend =
      tipoPromo === "precio_fijo"
        ? String(descuentoValor)
        : requiereValor
        ? String(valorPromo)
        : "";

    const formData = new FormData();
    formData.append("product_id", producto.id);
    formData.append("promotion_type", tipoPromo);
    formData.append("value", valueToSend);
    formData.append("start_date", fechaInicio);
    formData.append("end_date", sinFechaFin ? "" : fechaFin);
    formData.append("no_end", String(sinFechaFin));

    try {
      const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
      const resp = await fetch("/ofertas/crear/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setAlerta({ open: true, message: data.message, type: "success" });
        limpiarProducto();
      } else {
        throw new Error(data.error || "Error al crear la promoción.");
      }
    } catch (err) {
      setAlerta({ open: true, message: err.message, type: "error" });
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const intentarSeleccionarPorEnter = () => {
    const raw = (busqueda || "").trim();
    if (!raw) return;

    const exact = productos.find((p) => {
      const code = String(p?.code || "").trim();
      const barcode = String(p?.barcode || "").trim();
      return code === raw || barcode === raw;
    });

    if (exact) {
      seleccionarProducto(exact);
      return;
    }

    if (productos.length === 1) {
      seleccionarProducto(productos[0]);
      return;
    }

    const byName = productos.find((p) => String(p?.name || "").toLowerCase() === raw.toLowerCase());
    if (byName) seleccionarProducto(byName);
  };

  const renderTotalRow = (label, value) => (
    <React.Fragment key={label}>
      <Grid item xs={7}>
        <Typography sx={{ color: "#333" }}>{label}:</Typography>
      </Grid>
      <Grid item xs={5}>
        <TextField
          value={value}
          fullWidth
          size="small"
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          sx={{
            "& .Mui-disabled": {
              backgroundColor: "#f0f0f0 !important",
              color: "#333 !important",
              WebkitTextFillColor: "#333 !important",
            },
          }}
        />
      </Grid>
    </React.Fragment>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate(-1)}
            >
              Regresar
            </Button>
          </Box>

          <Box sx={{ bgcolor: "#f5f5f5" }}>
            <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", bgcolor: "#FFFFFF" }} elevation={0}>
              <Typography variant="h6" sx={{ mb: 2, color: "#000" }}>
                Promociones
              </Typography>

              <Autocomplete
                options={productos}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.name} (${o.code})`)}
                inputValue={busqueda}
                open={openAuto}
                onOpen={() => busqueda.length >= 2 && !producto && setOpenAuto(true)}
                onClose={() => setOpenAuto(false)}
                onInputChange={(_, v, reason) => {
                  if (reason === "input") {
                    setBusqueda(v);
                    setProducto(null);
                    setOpenAuto(v.length >= 2);
                  }
                }}
                onChange={(_, v) => {
                  if (typeof v === "string") {
                    setBusqueda(v);
                    intentarSeleccionarPorEnter();
                    return;
                  }
                  if (v && typeof v === "object") seleccionarProducto(v);
                }}
                noOptionsText="No hay resultados"
                freeSolo
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar producto..."
                    sx={{ width: "50%" }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        intentarSeleccionarPorEnter();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={limpiarProducto} edge="end">
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {producto && (
                <>
                  <Paper sx={{ p: 2, my: 3, borderRadius: "12px", bgcolor: "#f5f5f5" }} elevation={0}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md="auto">
                        <Chip label={producto.code} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={producto.brand} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={`Stock: ${producto.stock}`} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={`PVP: ${money(precioBase)}`} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={`IVA %: ${ivaPctActual}`} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={`IVA $: ${money(ivaFijoProducto)}`} />
                      </Grid>
                      <Grid item xs={12} md="auto">
                        <Chip label={`Total: ${money(totalProducto)}`} />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Select
                        fullWidth
                        value={tipoPromo}
                        onChange={(e) => {
                          setTipoPromo(e.target.value);
                          setValorPromo("");
                        }}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Tipo de promoción</em>
                        </MenuItem>
                        {PROMO_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    {requiereValor && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label={tipoPromo === "descuento" ? "Porcentaje (%)" : "Precio oferta (con IVA)"}
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, step: "0.01" }}
                          value={valorPromo}
                          onChange={(e) => setValorPromo(e.target.value)}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                      <FormControlLabel
                        control={<Switch checked={sinFechaFin} onChange={(e) => setSinFechaFin(e.target.checked)} />}
                        label="Sin fecha de fin"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Inicio"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                      />
                    </Grid>

                    {!sinFechaFin && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Fin"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={fechaFin}
                          onChange={(e) => setFechaFin(e.target.value)}
                        />
                      </Grid>
                    )}

                    {tipoPromo && (
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: "12px", minWidth: { xs: "100%", sm: 520 } }} elevation={0}>
                            <Grid container spacing={1}>
                              {renderTotalRow("Subtotal", money(subtotalPromo))}
                              {renderTotalRow("Descuento", money(descuentoValor))}
                              {renderTotalRow(`IVA (${ivaPctActual}%)`, money(ivaPromo))}
                              {renderTotalRow("Total", money(totalPromo))}
                            </Grid>
                          </Paper>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  <Box mt={4}>
                    <Button
                      variant="contained"
                      onClick={handleGuardar}
                      sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                      disabled={!producto}
                    >
                      Guardar promoción
                    </Button>
                  </Box>
                </>
              )}
            </Paper>

            <Snackbar
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              open={alerta.open}
              autoHideDuration={3000}
              onClose={() => setAlerta({ ...alerta, open: false })}
            >
              <Alert severity={alerta.type} onClose={() => setAlerta({ ...alerta, open: false })} sx={{ width: "100%" }}>
                {alerta.message}
              </Alert>
            </Snackbar>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
